import { Timestamp } from "firebase/firestore";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../firebase";

export interface AuditTrailEntry {
  id: string;
  businessId: string;
  userId: string;
  action: AuditAction;
  targetType: AuditTargetType;
  targetId: string;
  changes: Record<string, any>;
  timestamp: Timestamp;
  metadata: Record<string, any>;
}

export enum AuditAction {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  ASSIGN = "ASSIGN",
  REVOKE = "REVOKE",
}

export enum AuditTargetType {
  PERMISSION = "PERMISSION",
  ROLE = "ROLE",
  STAFF = "STAFF",
  BUSINESS = "BUSINESS",
}

export const createAuditEntry = async (
  businessId: string,
  userId: string,
  action: AuditAction,
  targetType: AuditTargetType,
  targetId: string,
  changes: Record<string, any>,
  metadata: Record<string, any> = {}
): Promise<void> => {
  const auditEntry: Omit<AuditTrailEntry, "id"> = {
    businessId,
    userId,
    action,
    targetType,
    targetId,
    changes,
    timestamp: new Date() as unknown as Timestamp,
    metadata,
  };

  await addDoc(collection(db, "auditTrail"), auditEntry);
};

export const getAuditTrail = async (
  businessId: string,
  filters: {
    action?: AuditAction;
    targetType?: AuditTargetType;
    startDate?: Date;
    endDate?: Date;
  } = {}
): Promise<AuditTrailEntry[]> => {
  const { collection, query, where, getDocs, orderBy } = await import(
    "firebase/firestore"
  );
  const { db } = await import("../../firebase");

  let q = query(
    collection(db, "auditTrail"),
    where("businessId", "==", businessId),
    orderBy("timestamp", "desc")
  );

  if (filters.action) {
    q = query(q, where("action", "==", filters.action));
  }

  if (filters.targetType) {
    q = query(q, where("targetType", "==", filters.targetType));
  }

  if (filters.startDate) {
    q = query(q, where("timestamp", ">=", filters.startDate));
  }

  if (filters.endDate) {
    q = query(q, where("timestamp", "<=", filters.endDate));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as AuditTrailEntry[];
};
